import React, { useEffect, useId, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { serverMessage } from '../error/server-message'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'
import { LocalTime } from '../date/local-time-view'
import { Pill } from '../sso-workspace/pill'
import { AI_TOOLS_ANSWERS, AI_TOOLS_QUESTION, AI_TOOLS_QUESTION_VERSION } from './question'
import { consentAvailable, consentState, getMyConsent, putMyConsent } from './consent-api'

// A person's own answer to the AI tools sharing question, in their account
// settings (consent visibility, owner-approved 2026-10-23). The same question,
// word for word and version, as the Caffeine tools ask, through the tools' own
// GET / PUT /api/ddd/ai-tools/consent: the answer is the account's, so it
// applies in the tools on every computer.
//
// Unanswered (no answer, or a share of an older question): the question with
// its two answers of equal weight, as the tools ask it. Answered: the status
// and the settings switch; turning it off asks first, because a stop unlinks
// what was already shared. The signed-in organization's switch is told, never
// folded into the answer. An impersonated session neither sees nor answers it
// (the server refuses both).

export const AI_TOOLS_SWITCH_LABEL = 'Share AI tools usage data'
export const AI_TOOLS_STOP_CONFIRM = 'If you stop sharing, nothing more is collected and what you already shared is no longer linked to your account.'
export const AI_TOOLS_ORG_BLOCKS = "Your current organization doesn't allow AI tools usage data, so nothing from work in it is shared, whatever you choose here."

const STATUS = {
  sharing: { tone: 'ok', text: 'Sharing' },
  not_sharing: { tone: '', text: 'Not sharing' },
  unanswered: { tone: 'warn', text: 'Not answered' }
}

export const MyAIToolsSharing = () => {
  const { state } = useAuthContext()
  const impersonating = !!(state && state.jwtClaims && state.jwtClaims.impersonationEndsAt)
  if (!consentAvailable()) return null
  if (impersonating) {
    return <p className="sw-hint small mb-0" data-testid="my-ai-tools-sharing">You can't see or change this while you're viewing as someone else.</p>
  }
  return <MyAIToolsSharingAnswer />
}

const MyAIToolsSharingAnswer = () => {
  const id = useId()
  const [consent, setConsent] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    let live = true
    Promise.resolve().then(() => getMyConsent()).then(
      c => { if (live) { setError(null); setConsent(c || {}) } },
      err => { if (live) { setConsent(null); setError(serverMessage(err, "Your AI tools sharing answer couldn't be loaded. Try again in a moment."), err) } }
    )
    return () => { live = false }
  }, [setError])

  const answer = async choice => {
    if (busy) return
    setBusy(true)
    setSaveError(null)
    try {
      const saved = await putMyConsent(choice)
      // PUT answers the GET's shape without the organization's switch: keep it.
      setConsent(previous => ({ ...previous, ...(saved || { choice, questionVersion: AI_TOOLS_QUESTION_VERSION }) }))
      setConfirming(false)
    } catch (err) {
      setSaveError(serverMessage(err, "Your answer couldn't be saved. Try again in a moment."), err)
    }
    setBusy(false)
  }

  const status = consent ? consentState(consent) : null
  const pill = status && STATUS[status]

  return (
    <div className="ai-tools-consent" data-testid="my-ai-tools-sharing">
      <p className="fw-semibold mb-1">{AI_TOOLS_QUESTION.title}</p>
      <p className="small mb-1">{AI_TOOLS_QUESTION.what}</p>
      <p className="small mb-1">{AI_TOOLS_QUESTION.never}</p>
      <p className="small mb-2">{AI_TOOLS_QUESTION.account}</p>

      {error && <p className="text-danger small mb-1" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} />}
      {consent === undefined && !error && <p className="text-body-secondary small mb-0" role="status">Loading…</p>}

      {consent &&
        <>
          <p className="small mb-2" data-testid="ai-tools-consent-status">
            <Pill tone={pill.tone}>{pill.text}</Pill>
            {consent.answeredAt && status !== 'unanswered' && <span className="text-body-secondary ms-2">Answered <LocalTime value={consent.answeredAt} /></span>}
          </p>

          {status === 'unanswered' &&
            <div className="d-flex gap-2" role="group" aria-label={AI_TOOLS_QUESTION.title}>
              <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => answer('stopped')}>{AI_TOOLS_ANSWERS.dontShare}</button>
              <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => answer('share')}>{AI_TOOLS_ANSWERS.share}</button>
            </div>}

          {status !== 'unanswered' &&
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={id}
                checked={status === 'sharing'}
                disabled={busy || confirming}
                onChange={() => (status === 'sharing' ? setConfirming(true) : answer('share'))}
              />
              <label className="form-check-label" htmlFor={id}>{AI_TOOLS_SWITCH_LABEL}</label>
            </div>}

          {confirming &&
            <div className="border rounded p-2 mt-2" role="group" aria-label="Stop sharing?">
              <p className="small mb-2">{AI_TOOLS_STOP_CONFIRM}</p>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => answer('stopped')}>{AI_TOOLS_ANSWERS.stopSharing}</button>
                <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
              </div>
            </div>}

          {consent.organizationAllows === false && <p className="sw-hint small mt-2 mb-0" data-testid="ai-tools-org-blocks">{AI_TOOLS_ORG_BLOCKS}</p>}
          {saveError && <p className="text-danger small mt-2 mb-1" role="alert">{saveError}</p>}
          {saveError && <ErrorReference error={saveFailure} />}
          <p className="text-body-secondary small mt-2 mb-0">Question version {AI_TOOLS_QUESTION_VERSION}</p>
        </>}
    </div>
  )
}
